import {
  Receipt,
  Briefcase,
  Users,
  FileText,
  Shield,
  Heart,
  CheckSquare,
  Home,
  Scale,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
  type LucideIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@educa-legal/ui';
import { services, servicePath, type ServiceTone } from '../data/services';

const icons: Record<string, LucideIcon> = {
  'reforma-tributaria-escolas':      Receipt,
  'juridico-interno-escolas':        Briefcase,
  'direito-trabalhista-educacional': Users,
  'contratos-inadimplencia-escolar': FileText,
  'lgpd-escolas':                    Shield,
  'inclusao-escolar-legislacao':     Heart,
  'compliance-escolar':              CheckSquare,
  'direito-imobiliario-escolas':     Home,
  'gestao-contencioso-escolar':      Scale,
  'ma-educacional':                  TrendingUp,
};

const toneIconMap: Record<ServiceTone, string> = {
  orange:  'text-orange',
  sky:     'text-sky',
  grape:   'text-grape',
  olive:   'text-olive',
  mustard: 'text-mustard',
};

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

const [featured, ...rest] = services;
const FeaturedIcon = icons[featured.slug];

export function Services() {
  return (
    <section id="servicos" className="relative py-10 md:py-12 overflow-hidden scroll-mt-16 md:scroll-mt-20">
      {/* Pattern editorial sutil — DNA do site */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative">
        {/* Header */}
        <motion.div
          className="mb-5 md:mb-6 max-w-3xl"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-48px' }}
          transition={{ duration: 0.55 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <motion.span
              className="h-px bg-orange origin-left"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{ width: 40 }}
            />
            <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-700">
              Serviços
            </span>
          </div>
          <h2 className="text-display-sm md:text-display-md font-bold text-neutral-950 leading-tight tracking-tight">
            Uma escola tem muitas frentes jurídicas. A Educa Legal cuida de todas elas.
          </h2>
        </motion.div>

        {/* Card de destaque — entrada 01 do índice */}
        <Link
          to={servicePath(featured.slug)}
          id={`servicos-${featured.slug}`}
          className="block scroll-mt-24 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2 rounded-r-2xl"
        >
          <motion.article
            className="group relative bg-orange-50/50 border-l-4 border-orange rounded-r-2xl px-5 md:px-6 py-3.5 flex items-center gap-4 md:gap-5 transition-colors duration-200 hover:bg-orange-50"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-32px' }}
            transition={{ duration: 0.45 }}
          >
            {/* Numeração editorial */}
            <span className="text-display-sm md:text-display-md font-bold text-orange tabular-nums leading-none flex-shrink-0">
              01
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-caption font-semibold text-orange uppercase tracking-[0.18em]">
                  Em destaque
                </span>
                <span aria-hidden className="h-px w-4 bg-orange/40" />
                <span className="text-caption uppercase tracking-wider text-orange-700">
                  Prioridade 2025–2026
                </span>
              </div>
              <h3 className="font-semibold text-heading-md md:text-heading-lg text-neutral-950 leading-snug">
                {featured.title}
              </h3>
              <p className="hidden lg:block mt-0.5 text-body-sm text-neutral-600 leading-snug line-clamp-1">
                {featured.tagline}
              </p>
            </div>

            {/* Ícone discreto + arrow */}
            <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
              <FeaturedIcon size={18} className="text-orange/60" />
              <ArrowUpRight
                size={20}
                className="text-orange-600 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </div>
          </motion.article>
        </Link>

        {/* Grid 3×3 — entradas 02-10 do índice */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8">
          {rest.map((service, i) => {
            const Icon = icons[service.slug];
            const iconColor = toneIconMap[service.tone];
            const num = String(i + 2).padStart(2, '0');

            return (
              <Link
                key={service.slug}
                to={servicePath(service.slug)}
                id={`servicos-${service.slug}`}
                className="group block scroll-mt-24 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-4 focus-visible:ring-offset-white rounded-sm"
              >
                <motion.article
                  className="relative pt-3 pb-3.5 border-t-2 border-neutral-200 transition-colors duration-200 group-hover:border-orange"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-24px' }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  {/* Top row: número + ícone */}
                  <div className="flex items-start justify-between mb-1.5">
                    <span className="text-heading-lg font-bold tabular-nums leading-none text-neutral-300 group-hover:text-orange transition-colors">
                      {num}
                    </span>
                    <Icon
                      size={14}
                      className={`flex-shrink-0 mt-1 opacity-40 group-hover:opacity-100 transition-opacity ${iconColor}`}
                    />
                  </div>

                  {/* Título */}
                  <h3 className="font-semibold text-body-md text-neutral-950 leading-snug group-hover:text-orange-700 transition-colors min-h-[2.6em]">
                    {service.title}
                  </h3>

                  {/* Tagline */}
                  <p className="mt-1.5 text-body-sm text-neutral-500 leading-snug line-clamp-2">
                    {service.tagline}
                  </p>

                  {/* "Saiba mais" — aparece no hover */}
                  <span className="inline-flex items-center gap-1 mt-2 text-caption font-semibold uppercase tracking-wider text-neutral-400 group-hover:text-orange group-hover:gap-2 transition-all">
                    Saiba mais
                    <ArrowRight size={12} />
                  </span>
                </motion.article>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-6 md:mt-8 flex justify-center"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Button asChild variant="primary" size="default">
            <a href="https://wa.me/5511989082892" target="_blank" rel="noopener noreferrer">
              Fale conosco pelo WhatsApp
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
